import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import './PublicTracking.css';

/**
 * PUBLIC TRACKING PAGE
 * Publik spårningssida för kunder att följa sina paket
 * Accessbar via: /track/:trackingNumber
 */
const normalizeLang = (lang) => {
  if (!lang) return null;
  return String(lang).split('-')[0].toLowerCase();
};

const PUBLIC_LANG_STORAGE_KEY = 'itraxx_public_lang';

const PublicTracking = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [languageReady, setLanguageReady] = useState(false);
  const requestedLang = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return normalizeLang(
      params.get('lang') || params.get('language') || params.get('locale') || params.get('lng')
    );
  }, [location.search]);
  const storedLang = useMemo(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    return normalizeLang(window.localStorage.getItem(PUBLIC_LANG_STORAGE_KEY));
  }, []);
  const activeLang = requestedLang || storedLang || normalizeLang(i18n.resolvedLanguage || i18n.language) || 'en';

  const trackPackage = useCallback(async (tn) => {
    const number = tn;
    if (!number || number.length < 5) {
      setError(t('public_tracking.errors.invalid_number'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/goods/track/${encodeURIComponent(number)}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('public_tracking.errors.not_found'));
      }

      setTrackingData(data);
    } catch (err) {
      setError(err.message);
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    const nextLang = requestedLang || storedLang;
    const currentLang = normalizeLang(i18n.resolvedLanguage || i18n.language);

    if (!nextLang) {
      setLanguageReady(true);
      return;
    }

    if (nextLang === currentLang) {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(PUBLIC_LANG_STORAGE_KEY, nextLang);
      }
      setLanguageReady(true);
      return;
    }

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(PUBLIC_LANG_STORAGE_KEY, nextLang);
    }

    setLanguageReady(false);
    i18n.changeLanguage(nextLang)
      .catch(() => {})
      .finally(() => setLanguageReady(true));
  }, [i18n, requestedLang, storedLang]);

  useEffect(() => {
    if (!languageReady) {
      return;
    }

    document.documentElement.lang = (i18n.resolvedLanguage || 'en').split('-')[0];
    document.title = t('public_tracking.header.title');
  }, [i18n.resolvedLanguage, languageReady, t]);

  // Auto-load tracking number from URL if present
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tn = urlParams.get('tn') || location.pathname.split('/track/')[1];
    if (tn) {
      setTrackingNumber(tn);
      trackPackage(tn);
    }
  }, [location.pathname, location.search, trackPackage]);

  const handleSubmit = (e) => {
    e.preventDefault();
    trackPackage(trackingNumber);
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending_dispatch: '🛰️',
      assigned: '🚕',
      accepted: '👍',
      arrived_pickup: '📍',
      picked_up: '🚚',
      pending: '⏳',
      in_progress: '🚚',
      completed: '✅',
      failed: '❌',
      created: '📦'
    };
    return icons[status] || '📦';
  };

  const getStatusText = (status) => {
    const texts = {
      pending_dispatch: t('public_tracking.status.pending_dispatch'),
      assigned: t('public_tracking.status.assigned'),
      accepted: t('public_tracking.status.accepted'),
      arrived_pickup: t('public_tracking.status.arrived_pickup'),
      picked_up: t('public_tracking.status.picked_up'),
      pending: t('public_tracking.status.pending'),
      in_progress: t('public_tracking.status.in_progress'),
      completed: t('public_tracking.status.completed'),
      failed: t('public_tracking.status.failed'),
      created: t('public_tracking.status.created')
    };
    return texts[status] || status;
  };

  const getEventIcon = (eventType) => {
    const icons = {
      created: '📦',
      picked_up: '📤',
      in_transit: '🚚',
      out_for_delivery: '🚛',
      delivered: '✅',
      failed: '❌',
      returned: '↩️'
    };
    return icons[eventType] || '•';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString(i18n.resolvedLanguage || i18n.language || 'en', {
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } else if (days > 0) {
      return t('public_tracking.time.days_ago', { count: days });
    } else if (hours > 0) {
      return t('public_tracking.time.hours_ago', { count: hours });
    } else {
      const minutes = Math.floor(diff / (1000 * 60));
      return t('public_tracking.time.minutes_ago', { count: minutes });
    }
  };

  return (
    <div className="public-tracking-page">
      {!languageReady ? null : (
        <>
      <div className="tracking-header">
        <div className="tracking-logo">
          <h1>{t('public_tracking.header.title')}</h1>
        </div>
      </div>

      <div className="tracking-search-container">
        <form onSubmit={handleSubmit} className="tracking-search-form">
          <input
            type="text"
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            placeholder={t('public_tracking.search.placeholder')}
            className="tracking-input"
          />
          <button type="submit" className="tracking-search-btn" disabled={loading}>
            {loading ? t('public_tracking.search.loading') : t('public_tracking.search.submit')}
          </button>
        </form>

        {error && (
          <div className="tracking-error">
            ⚠️ {error}
          </div>
        )}
      </div>

      {trackingData && (
        <div className="tracking-results">
          {/* Status Overview */}
          <div className="tracking-status-card">
            <div className="status-main">
              <div className="status-icon-large">
                {getStatusIcon(trackingData.order.status)}
              </div>
              <div className="status-text">
                <h2>{getStatusText(trackingData.order.status)}</h2>
                <p className="status-subtitle">
                  {t('public_tracking.labels.tracking_number')}: {trackingData.order.tracking_number}
                </p>
              </div>
            </div>

            {trackingData.order.status === 'completed' && (
              <div className="delivery-success">
                {t('public_tracking.completed_banner')}
              </div>
            )}
          </div>

          {/* Package Details */}
          {trackingData.package && (
            <div className="tracking-details-card">
              <h3>{t('public_tracking.package.title')}</h3>
              <div className="details-grid">
                {trackingData.package.weight && (
                  <div className="detail-item">
                    <span className="detail-label">{t('public_tracking.package.weight')}</span>
                    <span className="detail-value">{trackingData.package.weight} kg</span>
                  </div>
                )}
                {trackingData.package.dimensions_length && (
                  <div className="detail-item">
                    <span className="detail-label">{t('public_tracking.package.dimensions')}</span>
                    <span className="detail-value">
                      {trackingData.package.dimensions_length} × {trackingData.package.dimensions_width} × {trackingData.package.dimensions_height} cm
                    </span>
                  </div>
                )}
                {trackingData.package.content_description && (
                  <div className="detail-item">
                    <span className="detail-label">{t('public_tracking.package.content')}</span>
                    <span className="detail-value">{trackingData.package.content_description}</span>
                  </div>
                )}
                {trackingData.package.reference_number && (
                  <div className="detail-item">
                    <span className="detail-label">{t('public_tracking.package.reference')}</span>
                    <span className="detail-value">{trackingData.package.reference_number}</span>
                  </div>
                )}
              </div>

              {/* Special Handling */}
              {trackingData.package.special_handling && (() => {
                try {
                  const handling = JSON.parse(trackingData.package.special_handling);
                  const flags = Object.entries(handling).filter(([k, v]) => v === true);
                  
                  if (flags.length > 0) {
                    return (
                      <div className="special-handling">
                        <h4>{t('public_tracking.package.special_handling')}</h4>
                        <div className="handling-flags">
                          {handling.fragile && <span className="flag">{t('public_tracking.package.flags.fragile')}</span>}
                          {handling.cold && <span className="flag">{t('public_tracking.package.flags.cold')}</span>}
                          {handling.frozen && <span className="flag">{t('public_tracking.package.flags.frozen')}</span>}
                          {handling.adr && <span className="flag">{t('public_tracking.package.flags.adr')}</span>}
                          {handling.timeCritical && <span className="flag">{t('public_tracking.package.flags.time_critical')}</span>}
                          {handling.idRequired && <span className="flag">{t('public_tracking.package.flags.id_required')}</span>}
                          {handling.highValue && <span className="flag">{t('public_tracking.package.flags.high_value')}</span>}
                        </div>
                      </div>
                    );
                  }
                } catch (e) {
                  return null;
                }
              })()}
            </div>
          )}

          {/* Tracking Timeline */}
          <div className="tracking-timeline-card">
            <h3>{t('public_tracking.timeline.title')}</h3>
            <div className="timeline">
              {trackingData.events && trackingData.events.length > 0 ? (
                trackingData.events.map((event, index) => (
                  <div key={event.id} className={`timeline-event ${index === 0 ? 'latest' : ''}`}>
                    <div className="event-icon">
                      {getEventIcon(event.event_type)}
                    </div>
                    <div className="event-content">
                      <div className="event-description">{event.event_description || event.event_type}</div>
                      {event.event_location && (
                        <div className="event-location">📍 {event.event_location}</div>
                      )}
                      <div className="event-time">{formatDate(event.created_at)}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-events">
                  {t('public_tracking.timeline.empty')}
                </div>
              )}
            </div>
          </div>

          {/* Addresses */}
          <div className="tracking-addresses-card">
            <div className="address-row">
              <div className="address-col">
                <h4>{t('public_tracking.address.from')}</h4>
                <p>{trackingData.order.pickup_address} {trackingData.order.pickup_number}</p>
              </div>
              <div className="address-arrow">→</div>
              <div className="address-col">
                <h4>{t('public_tracking.address.to')}</h4>
                <p>{trackingData.order.dropoff_address} {trackingData.order.dropoff_number}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {!trackingData && !loading && !error && (
        <div className="tracking-placeholder">
          <div className="placeholder-icon" aria-hidden="true">
            <svg viewBox="0 0 120 120" focusable="false">
              <defs>
                <linearGradient id="trackingPlaceholderGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="100%" stopColor="#dbeafe" />
                </linearGradient>
              </defs>
              <rect x="16" y="22" width="88" height="76" rx="22" fill="rgba(255,255,255,0.12)" />
              <path
                d="M36 77c8-18 16-28 27-28 9 0 15 6 21 6 6 0 10-4 16-14"
                fill="none"
                stroke="url(#trackingPlaceholderGradient)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray="10 12"
              />
              <circle cx="34" cy="80" r="10" fill="#34d399" />
              <circle cx="34" cy="80" r="4" fill="#08111e" />
              <path d="M85 33c-10.2 0-18 7.7-18 17.4 0 12.1 18 31.3 18 31.3s18-19.2 18-31.3C103 40.7 95.2 33 85 33z" fill="#ffffff" />
              <circle cx="85" cy="50" r="6" fill="#667eea" />
            </svg>
          </div>
          <h3>{t('public_tracking.placeholder.title')}</h3>
          <p>{t('public_tracking.placeholder.body')}</p>
        </div>
      )}

        </>
      )}
    </div>
  );
};

export default PublicTracking;
